import type { ClientType } from '@shared/documents'
import { miniApp } from '@tma.js/sdk-react'
import {
  type ChangeEvent,
  type RefObject,
  type SubmitEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import successImage from '@/assets/payment-qrcode.png'
import {
  getStepConfigs,
  OCR_FIELDS_BY_PAGE,
  OCR_SUCCESS_MESSAGE_BY_PAGE,
  type PassportOcrData,
  type PassportOcrPage,
  SUPPLIER_FIELD_KEYS,
} from '@/configs/docs-form'
import {
  type ApiMe,
  type ApiSupplier,
  fetchDocumentRequest,
  fetchTeamSuppliers,
  submitDocuments,
  updateDocumentRequest,
  uploadPassportImage,
} from '@/fetcher'
import { type DocsFormValues, getFormInitState } from '@/helpers/docs-form'
import { useDocsFormMasks } from '@/hooks/useDocsFormMasks'
import { useNav } from '@/nav'
import { DocsFormReviewSections } from './DocsFormReviewSections'
import { DocsFormStepSections } from './DocsFormStepSections'
import { IntroStepSections } from './IntroStepSections'
import { StatusToast } from './StatusToast'
import { SupplierFormPage } from './SupplierFormPage'
import { SupplierListPage } from './SupplierListPage'
import { TelegramBackButton } from './TelegramBackButton'
import { TelegramMainButton } from './TelegramMainButton'

type SupplierView = 'none' | 'list' | 'form'

const INTRO_STEP_COUNT = 1

const parseDecimalValue = (value: string) => {
  const parsedValue = Number(value.replace(',', '.'))
  return Number.isFinite(parsedValue) ? parsedValue : null
}

const formatConversionRate = (rate: number) =>
  Number(rate.toFixed(12)).toString()

const calculateConversionRate = (invoiceAmount: string, amountRub: string) => {
  const parsedInvoiceAmount = parseDecimalValue(invoiceAmount)
  const parsedAmountRub = (parseDecimalValue(amountRub) ?? 0) - 10_000

  if (
    parsedInvoiceAmount === null ||
    parsedAmountRub < 0 ||
    parsedInvoiceAmount === 0
  ) {
    return null
  }

  return formatConversionRate(parsedAmountRub / parsedInvoiceAmount)
}

type DocsFormProps = {
  me: ApiMe | null
  isLoadingMe: boolean
}

const DocsForm = ({ me, isLoadingMe }: DocsFormProps) => {
  const { navigate, state } = useNav()
  const editingRequestId = state.params.requestId ?? ''
  const [values, setValues] = useState(getFormInitState())
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [selectedSupplierId, setSelectedSupplierId] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [uploadingPage, setUploadingPage] = useState<PassportOcrPage | null>(
    null,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [selectedTeamId, setSelectedTeamId] = useState('')
  const [suppliers, setSuppliers] = useState<ApiSupplier[]>([])
  const [contractNo, setContractNo] = useState<string | null>(null)
  const [isSuppliersLoading, setIsSuppliersLoading] = useState(false)
  const [supplierView, setSupplierView] = useState<SupplierView>('none')
  const [editingSupplier, setEditingSupplier] = useState<ApiSupplier | null>(
    null,
  )
  const [isSupplierBusy, setIsSupplierBusy] = useState(false)
  const prefillTeamIdRef = useRef<string | null>(null)
  const submitRef = useRef<(() => void) | null>(null)
  const maskedRefs = useDocsFormMasks()

  useEffect(() => {
    if (!me) return
    if (me.teams.length === 1 && me.teams[0]?.id)
      setSelectedTeamId(me.teams[0].id)
  }, [me])

  useEffect(() => {
    if (!editingRequestId) return
    let isMounted = true

    fetchDocumentRequest(editingRequestId)
      .then((payload) => {
        if (!isMounted) return
        const r = payload.request
        prefillTeamIdRef.current = r.teamId
        setSelectedTeamId(r.teamId)
        setValues({
          ...getFormInitState(),
          ...r.data,
          legalType: r.data.legalType ?? 'ip',
        })
        const stepCount = getStepConfigs(
          r.data.clientType as ClientType,
          r.data.legalType ?? 'ip',
        ).length
        setCurrentStepIndex(stepCount + INTRO_STEP_COUNT)
      })
      .catch((error) => {
        if (!isMounted) return
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Не удалось загрузить заявку для редактирования',
        )
      })

    return () => {
      isMounted = false
    }
  }, [editingRequestId])

  useEffect(() => {
    let isMounted = true

    setSelectedSupplierId('')
    setSuppliers([])

    const isPrefillingTeam =
      prefillTeamIdRef.current !== null &&
      prefillTeamIdRef.current === selectedTeamId

    if (isPrefillingTeam) {
      prefillTeamIdRef.current = null
    } else {
      setValues((currentValues) => {
        const nextValues = { ...currentValues }
        for (const key of SUPPLIER_FIELD_KEYS) {
          nextValues[key] = ''
        }
        return nextValues
      })
    }

    if (!selectedTeamId) {
      return () => {
        isMounted = false
      }
    }

    setIsSuppliersLoading(true)
    fetchTeamSuppliers(selectedTeamId)
      .then((payload) => {
        if (!isMounted) return
        setSuppliers(payload.suppliers)
      })
      .catch((error) => {
        if (!isMounted) return
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Не удалось загрузить поставщиков',
        )
      })
      .finally(() => {
        if (isMounted) setIsSuppliersLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [selectedTeamId])

  const updateValue = (key: keyof DocsFormValues, value: string) => {
    setValues((currentValues) => {
      const nextValues = {
        ...currentValues,
        [key]: value,
      }

      if (key === 'invoiceAmount' || key === 'amountRub') {
        const conversionRate = calculateConversionRate(
          nextValues.invoiceAmount,
          nextValues.amountRub,
        )

        if (conversionRate !== null) {
          nextValues.conversionRate = conversionRate
        }
      }

      return nextValues
    })
  }

  const applySupplier = (supplierId: string) => {
    setSelectedSupplierId(supplierId)

    const supplier = suppliers.find((item) => item.id === supplierId)
    if (!supplier) {
      return
    }

    setValues((currentValues) => {
      const nextValues = { ...currentValues }

      for (const key of SUPPLIER_FIELD_KEYS) {
        nextValues[key] = supplier.values[key]
      }

      return nextValues
    })
  }

  const openSupplierList = () => {
    if (!selectedTeamId) return
    setEditingSupplier(null)
    setSupplierView('list')
  }

  const openCreateSupplier = () => {
    setEditingSupplier(null)
    setSupplierView('form')
  }

  const openEditSupplier = (supplier: ApiSupplier) => {
    setEditingSupplier(supplier)
    setSupplierView('form')
  }

  const closeSupplierList = () => {
    setSupplierView('none')
  }

  const closeSupplierForm = () => {
    setEditingSupplier(null)
    setSupplierView('list')
  }

  const handleSupplierSaved = (supplier: ApiSupplier) => {
    setSuppliers((current) => {
      const exists = current.some((item) => item.id === supplier.id)
      return exists
        ? current.map((item) => (item.id === supplier.id ? supplier : item))
        : [...current, supplier]
    })
    applySupplier(supplier.id)
    setEditingSupplier(null)
    setSupplierView('list')
  }

  const handleSupplierDeleted = (supplierId: string) => {
    setSuppliers((current) => current.filter((item) => item.id !== supplierId))
    if (selectedSupplierId === supplierId) {
      setSelectedSupplierId('')
      setValues((currentValues) => {
        const nextValues = { ...currentValues }
        for (const key of SUPPLIER_FIELD_KEYS) {
          nextValues[key] = ''
        }
        return nextValues
      })
    }
    setEditingSupplier(null)
    setSupplierView('list')
  }

  const handleSupplierBusyChange = useCallback((isBusy: boolean) => {
    setIsSupplierBusy(isBusy)
  }, [])

  const applyOcrValues = (page: PassportOcrPage, data: PassportOcrData) => {
    const fieldKeys = OCR_FIELDS_BY_PAGE[page]

    setValues((currentValues) => {
      const nextValues = { ...currentValues }

      for (const key of fieldKeys) {
        const value = data[key]
        if (typeof value !== 'string') {
          continue
        }

        const trimmedValue = value.trim()
        if (!trimmedValue) {
          continue
        }

        nextValues[key] = trimmedValue
      }

      return nextValues
    })
  }

  const handlePassportFileChange = async (
    page: PassportOcrPage,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const input = event.currentTarget
    const file = input.files?.[0]
    input.value = ''

    if (!file) {
      return
    }

    setErrorMessage(null)
    setSuccessMessage(null)
    setUploadingPage(page)

    try {
      const data = await uploadPassportImage(page, file)
      applyOcrValues(page, data)
      setSuccessMessage(OCR_SUCCESS_MESSAGE_BY_PAGE[page])
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Не удалось распознать изображение паспорта',
      )
    } finally {
      setUploadingPage((currentPage) =>
        currentPage === page ? null : currentPage,
      )
    }
  }

  const goToStep = (stepIndex: number) => {
    setCurrentStepIndex(stepIndex)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleNextStep = () => {
    setErrorMessage(null)
    setSuccessMessage(null)
    setCurrentStepIndex((stepIndex) => stepIndex + 1)
  }

  const handleSubmitDocuments = async () => {
    if (isSubmitting || isSubmitted) {
      return
    }

    setErrorMessage(null)
    setSuccessMessage(null)
    setIsSubmitting(true)

    try {
      if (!selectedTeamId) throw new Error('Выберите компанию')
      const response = editingRequestId
        ? await updateDocumentRequest(editingRequestId, values, selectedTeamId)
        : await submitDocuments(values, selectedTeamId)
      setContractNo(response.contractNo)
      setIsSubmitted(true)
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Не удалось отправить данные формы',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (currentStepIndex < totalScreens) {
      handleNextStep()
      return
    }

    await handleSubmitDocuments()
  }

  const handleMainPassportFileChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    void handlePassportFileChange('main', event)
  }

  const handleRegistrationFileChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    void handlePassportFileChange('registration', event)
  }

  const stepConfigs = getStepConfigs(values.clientType, values.legalType)
  const totalScreens = stepConfigs.length + INTRO_STEP_COUNT
  const isIntroStep = currentStepIndex === 0
  const isReviewStep = currentStepIndex === totalScreens
  const currentStep =
    !isIntroStep && !isReviewStep ? stepConfigs[currentStepIndex - 1] : null
  const isUploadingPassport = uploadingPage !== null
  const progressPercent = isReviewStep
    ? 100
    : ((currentStepIndex + 1) / totalScreens) * 100

  const handleMainAction = () => {
    if (currentStepIndex < totalScreens) {
      handleNextStep()
      return
    }

    void handleSubmitDocuments()
  }

  const canManageSuppliers = me?.user.role !== 'user'

  return (
    <>
      {!isSubmitted && !isLoadingMe && me && (
        <>
          {supplierView === 'none' ? (
            <TelegramBackButton
              isVisible
              onClick={() =>
                currentStepIndex === 0
                  ? navigate('requests-list')
                  : goToStep(currentStepIndex - 1)
              }
            />
          ) : supplierView === 'list' ? (
            <TelegramBackButton isVisible onClick={closeSupplierList} />
          ) : (
            <TelegramBackButton isVisible onClick={closeSupplierForm} />
          )}

          {supplierView === 'none' ? (
            <TelegramMainButton
              isVisible
              isLoading={isSubmitting}
              text={isReviewStep ? 'Отправить' : 'Далее'}
              onClick={handleMainAction}
            />
          ) : supplierView === 'list' ? (
            <TelegramMainButton
              isVisible={canManageSuppliers}
              isLoading={false}
              text="Добавить нового поставщика"
              onClick={openCreateSupplier}
            />
          ) : (
            <TelegramMainButton
              isVisible
              isLoading={isSupplierBusy}
              text="Сохранить"
              onClick={() => submitRef.current?.()}
            />
          )}
        </>
      )}
      <div className="py-8 mx-auto max-w-2xl px-4">
        {isLoadingMe ? (
          <div className="flex min-h-[70vh] items-center justify-center text-center text-[var(--app-muted)]">
            Загрузка профиля...
          </div>
        ) : isSubmitted ? (
          <div className="flex min-h-[70vh] flex-col items-center justify-center py-8">
            <div className="mx-auto max-w-50">
              <img alt="Документы" src={successImage} />
            </div>

            <div className="mt-6 space-y-3">
              <h1 className="text-xl text-center font-semibold tracking-tight text-[var(--app-text)]">
                Заявка успешно отправлена
              </h1>
              <p className="text-sm text-center leading-6 text-[var(--app-muted)]">
                QR-коды и PDF-документы придут вам в сообщении от Telegram-бота.
              </p>
              {contractNo && (
                <p className="rounded-[22px] bg-[var(--app-input)] px-4 py-3 text-center text-sm font-semibold text-[var(--app-text)]">
                  Номер договора: {contractNo}
                </p>
              )}
            </div>

            <button
              type="button"
              className="mt-8 inline-flex min-h-12 items-center justify-center rounded-full bg-[var(--app-accent)] px-5 py-3 text-sm font-semibold text-[var(--app-accent-text)] transition hover:opacity-90"
              onClick={() => {
                miniApp.close.ifAvailable()
              }}
            >
              Закрыть приложение
            </button>
          </div>
        ) : supplierView === 'list' && me && selectedTeamId ? (
          <SupplierListPage
            suppliers={suppliers}
            isLoading={isSuppliersLoading}
            selectedSupplierId={selectedSupplierId}
            canManage={canManageSuppliers}
            onSelect={(supplierId) => {
              applySupplier(supplierId)
              setSupplierView('none')
            }}
            onEdit={openEditSupplier}
          />
        ) : supplierView === 'form' && me && selectedTeamId ? (
          <SupplierFormPage
            teamId={selectedTeamId}
            supplier={editingSupplier}
            submitRef={submitRef as RefObject<(() => void) | null>}
            onBusyChange={handleSupplierBusyChange}
            onSaved={handleSupplierSaved}
            onDeleted={handleSupplierDeleted}
          />
        ) : (
          <>
            <form className="space-y-4" onSubmit={handleSubmit}>
              {me && me.teams.length > 1 && (
                <div className="space-y-2">
                  <label
                    className="block text-sm font-medium text-[var(--app-text)]"
                    htmlFor="team"
                  >
                    Команда
                  </label>

                  <div className="relative">
                    <select
                      className="w-full appearance-none rounded-[22px] bg-[var(--app-input)] px-4 py-3 pr-12 text-sm leading-6 text-[var(--app-text)] outline-none transition focus:border-[var(--app-accent)] focus:ring-4 focus:ring-[var(--app-accent-soft)] disabled:opacity-60"
                      id="team"
                      value={selectedTeamId}
                      disabled={Boolean(editingRequestId)}
                      onChange={(event) =>
                        setSelectedTeamId(event.target.value)
                      }
                    >
                      <option value="">Выберите команду</option>
                      {me.teams.map((team) => (
                        <option key={team.id} value={team.id}>
                          {team.name}
                        </option>
                      ))}
                    </select>

                    <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-[var(--app-muted)]">
                      ↓
                    </span>
                  </div>
                </div>
              )}
              {isIntroStep ? (
                <IntroStepSections
                  clientType={values.clientType}
                  legalType={values.legalType}
                  isUploadingPassport={isUploadingPassport}
                  isMainPassportLoading={uploadingPage === 'main'}
                  isRegistrationLoading={uploadingPage === 'registration'}
                  isSuppliersLoading={isSuppliersLoading}
                  suppliers={suppliers}
                  selectedSupplierId={selectedSupplierId}
                  onMainFileChange={handleMainPassportFileChange}
                  onRegistrationFileChange={handleRegistrationFileChange}
                  onClientTypeChange={(clientType) =>
                    updateValue('clientType', clientType)
                  }
                  onLegalTypeChange={(legalType) =>
                    updateValue('legalType', legalType)
                  }
                  onOpenSuppliers={openSupplierList}
                />
              ) : currentStep ? (
                <DocsFormStepSections
                  sections={currentStep}
                  values={values}
                  maskedRefs={maskedRefs}
                  onValueChange={updateValue}
                />
              ) : (
                <DocsFormReviewSections
                  stepConfigs={stepConfigs}
                  values={values}
                  onEditStep={(stepIndex) =>
                    goToStep(stepIndex + INTRO_STEP_COUNT)
                  }
                />
              )}
            </form>

            {!isReviewStep && (
              <div className="fixed inset-x-0 top-0 z-40">
                <div className="mx-auto max-w-2xl px-4 pt-2">
                  <div className="h-2 overflow-hidden rounded-full bg-[var(--app-accent-soft)]">
                    <div
                      className="h-full rounded-full bg-[var(--app-accent)] transition-[width] duration-300"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {errorMessage && (
        <StatusToast
          message={errorMessage}
          tone="error"
          onClose={() => setErrorMessage(null)}
        />
      )}

      {successMessage && (
        <StatusToast
          message={successMessage}
          tone="success"
          onClose={() => setSuccessMessage(null)}
        />
      )}
    </>
  )
}

export default DocsForm
